import React from "react";
import { Box, Button, Snackbar, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useSessionCapabilities } from "../../hooks/useSessionCapabilities";

const DISMISS_KEY = "inssa:limited-session-notice-dismissed:v1";

function readDismissed() {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, "1");
  } catch {}
}

export default function LimitedSessionNotice() {
  const { privateBrowsingLikely } = useSessionCapabilities();
  const [dismissed, setDismissed] = React.useState(readDismissed);

  const open = Boolean(!dismissed && privateBrowsingLikely);

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  if (!open) return null;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      sx={{
        mt: "calc(env(safe-area-inset-top, 0px) + 56px)",
        px: 1.5,
      }}
    >
      <Box
        role="status"
        sx={(theme) => ({
          width: "min(100%, 560px)",
          borderRadius: 1,
          px: 2,
          py: 1.75,
          color: theme.palette.mode === "dark" ? "#2a1b00" : "#3d2600",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #fff1b8 0%, #ffd88a 100%)"
              : "linear-gradient(135deg, #fff5cc 0%, #ffd98e 100%)",
          border: "1px solid rgba(166, 101, 0, 0.28)",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 18px 44px rgba(0, 0, 0, 0.35)"
              : "0 18px 42px rgba(101, 62, 0, 0.18)",
        })}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
          <WarningAmberRoundedIcon sx={{ mt: 0.2, color: "#7a4a00" }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.2, mb: 0.4 }}>
              Heads up about this browser session
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
              If you are using private browsing or strict privacy settings, sign-in, shared access, and app preferences may not save normally.
            </Typography>
          </Box>
        </Box>
        <Button
          fullWidth
          variant="contained"
          onClick={handleDismiss}
          sx={{
            mt: 1.5,
            py: 0.85,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 800,
            color: "#fff9e8",
            backgroundColor: "#6d4200",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#573500",
              boxShadow: "none",
            },
          }}
        >
          Got it
        </Button>
      </Box>
    </Snackbar>
  );
}
